import { useMemo, useState } from 'react';
import { createEmployeeTimeEntry, deleteAdminTimeEntry, markNoWorkDay, type TimeEntry } from '@/lib/api/portal';
import type { Employee } from '@/lib/api/employees';
import type { Job } from '@/lib/api/jobs';
import { useToast } from '@/lib/hooks/useToast';
import { ToastContainer } from '@/components/common/Toast';

export interface SaveResult { ok: number; failed: string[]; noWork?: boolean }

export type DayEntry = TimeEntry & { employee_email: string; employee_name: string };

// Worked hours from clock in/out minus break (handles crossing midnight).
const clockHours = (start: string, end: string, breakMin: number): number => {
  if (!start || !end) return 0;
  const toMin = (t: string) => { const [h, m] = t.split(':').map(Number); return (h || 0) * 60 + (m || 0); };
  let mins = toMin(end) - toMin(start);
  if (mins < 0) mins += 1440;
  mins -= breakMin;
  return Math.max(0, Math.round((mins / 60) * 100) / 100);
};

const hhmm = (t?: string | null) => (t ? String(t).slice(0, 5) : '');

interface WorkerRow {
  checked: boolean;
  override: boolean; // this worker worked different hours than the crew default
  start: string;
  end: string;
  lunch: boolean;
}

const DEFAULT_START = '08:00';
const DEFAULT_END = '16:30';

interface DayLogModalProps {
  date: string;       // YYYY-MM-DD
  dateLabel: string;  // e.g. "Mon, Jul 21, 2026"
  employees: Employee[];       // active, already sorted (myself first)
  myEmployeeId: string | null;
  jobs: Job[];
  existing: DayEntry[];        // entries already logged for this date
  defaultJobId?: number | null; // preselect this job in the crew-hours dropdown
  onClose: () => void;
  onSaved: (result: SaveResult) => void; // called after a successful save (parent closes + toasts)
  onChanged: () => void;                 // refresh parent data without closing (e.g. after delete)
}

export function DayLogModal({ date, dateLabel, employees, myEmployeeId, jobs, existing, defaultJobId, onClose, onSaved, onChanged }: DayLogModalProps) {
  const { toast, toasts, removeToast } = useToast();

  // Emails that already have an entry this day → badge + default-off.
  const loggedByEmail = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of existing) {
      const key = (e.employee_email || '').toLowerCase();
      m.set(key, (m.get(key) || 0) + Number(e.hours));
    }
    return m;
  }, [existing]);

  // Crew-wide defaults
  const [start, setStart] = useState(DEFAULT_START);
  const [end, setEnd] = useState(DEFAULT_END);
  const [lunch, setLunch] = useState(true);
  const [jobId, setJobId] = useState<string>(defaultJobId != null ? String(defaultJobId) : '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [noWork, setNoWork] = useState(false); // nobody worked this day

  // Per-worker rows keyed by employee id
  const [rows, setRows] = useState<Record<string, WorkerRow>>(() => {
    const init: Record<string, WorkerRow> = {};
    for (const emp of employees) {
      init[emp.id] = { checked: false, override: false, start: DEFAULT_START, end: DEFAULT_END, lunch: true };
    }
    return init;
  });

  const patchRow = (id: string, patch: Partial<WorkerRow>) =>
    setRows(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const crewHours = clockHours(start, end, lunch ? 30 : 0);

  // Effective clock for a worker: their override, else the crew default.
  const effective = (id: string) => {
    const r = rows[id];
    if (r.override) return { start: r.start, end: r.end, lunch: r.lunch };
    return { start, end, lunch };
  };
  const workerHours = (id: string) => {
    const eff = effective(id);
    return clockHours(eff.start, eff.end, eff.lunch ? 30 : 0);
  };

  const selected = employees.filter(e => rows[e.id]?.checked);
  const totalHours = selected.reduce((s, e) => s + workerHours(e.id), 0);

  const handleSave = async () => {
    if (noWork) {
      setSaving(true);
      try {
        await markNoWorkDay(date, notes.trim() || undefined);
        onSaved({ ok: 0, failed: [], noWork: true });
      } catch {
        toast.error('Failed to mark day as no work');
      } finally {
        setSaving(false);
      }
      return;
    }
    if (selected.length === 0) { toast.error('Select at least one worker'); return; }
    // Validate every selected worker resolves to > 0 hours.
    const bad = selected.filter(e => workerHours(e.id) <= 0);
    if (bad.length > 0) {
      toast.error(`Set a valid start/end for: ${bad.map(e => e.name).join(', ')}`);
      return;
    }
    setSaving(true);
    let ok = 0;
    const failed: string[] = [];
    for (const emp of selected) {
      const eff = effective(emp.id);
      const hrs = workerHours(emp.id);
      try {
        await createEmployeeTimeEntry(emp.id, {
          work_date: date,
          hours: hrs,
          break_minutes: eff.lunch ? 30 : 0,
          start_time: eff.start,
          end_time: eff.end,
          job_id: jobId ? Number(jobId) : null,
          notes: notes.trim() || undefined,
        });
        ok++;
      } catch {
        failed.push(emp.name);
      }
    }
    setSaving(false);
    if (ok > 0) {
      // Parent closes the modal, refreshes the calendar, and shows the summary toast
      // (a toast fired here would unmount with the modal before it could render).
      onSaved({ ok, failed });
    } else {
      toast.error(`Failed for: ${failed.join(', ')}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await deleteAdminTimeEntry(id);
      toast.success('Time entry deleted');
      onChanged();
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl bg-white rounded-t-2xl sm:rounded-xl shadow-xl max-h-[92vh] sm:max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Log hours</h2>
            <p className="text-sm text-gray-500">{dateLabel}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1">
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* No-work toggle */}
          <label className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer ${noWork ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-gray-50/60'}`}>
            <input type="checkbox" checked={noWork} onChange={e => setNoWork(e.target.checked)} />
            <span className="flex-1">
              <span className="block text-sm font-semibold text-gray-900">No one worked this day</span>
              <span className="block text-xs text-gray-500">Mark the day as no-work so it stops showing as a scheduled gap.</span>
            </span>
          </label>

          {noWork ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 text-sm text-amber-800">
              This day will be recorded as <strong>no work performed</strong>. No time entries are created.
              You can add an optional reason below.
            </div>
          ) : (
          <>
          {/* Crew defaults */}
          <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-gray-900">Crew hours</h3>
              <span className="text-xs text-gray-500">Applies to everyone below unless overridden</span>
            </div>
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">Start</span>
                <input type="time" value={start} onChange={e => setStart(e.target.value)}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-gray-500">End</span>
                <input type="time" value={end} onChange={e => setEnd(e.target.value)}
                  className="h-9 px-2 rounded-lg border border-gray-300 text-sm" />
              </label>
              <label className="flex items-center gap-2 h-9">
                <input type="checkbox" checked={lunch} onChange={e => setLunch(e.target.checked)} />
                <span className="text-sm text-gray-700">Deduct 30-min lunch</span>
              </label>
              <div className="ml-auto text-sm text-gray-600">
                Crew day: <span className="font-bold text-gray-900">{crewHours.toFixed(2)} h</span>
              </div>
            </div>
            <label className="flex flex-col gap-1 mt-3 max-w-xs">
              <span className="text-xs font-medium text-gray-500">Job (optional)</span>
              <select value={jobId} onChange={e => setJobId(e.target.value)}
                className="h-9 px-2 rounded-lg border border-gray-300 text-sm">
                <option value="">No specific job</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.title}</option>)}
              </select>
            </label>
          </div>

          {/* Workers */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">Who worked?</h3>
            <div className="space-y-1.5">
              {employees.map(emp => {
                const r = rows[emp.id];
                const already = loggedByEmail.get((emp.email || '').toLowerCase());
                const isMe = emp.id === myEmployeeId;
                return (
                  <div key={emp.id} className={`rounded-lg border ${r.checked ? 'border-primary/40 bg-primary/5' : 'border-gray-200'} px-3 py-2`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <label className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer">
                        <input type="checkbox" checked={r.checked}
                          onChange={e => patchRow(emp.id, { checked: e.target.checked })} />
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {emp.name}
                          {isMe && <span className="ml-1.5 text-xs font-semibold text-primary">(Myself)</span>}
                        </span>
                        <span className="text-xs text-gray-400 truncate hidden sm:inline">{emp.role}</span>
                      </label>

                      {already != null && (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 whitespace-nowrap">
                          already logged {already.toFixed(1)}h
                        </span>
                      )}

                      {r.checked && (
                        <>
                          <span className="text-sm font-semibold text-gray-900 tabular-nums">{workerHours(emp.id).toFixed(2)} h</span>
                          <button type="button"
                            onClick={() => patchRow(emp.id, { override: !r.override })}
                            className={`text-xs font-medium rounded-md px-2 py-1 border ${r.override ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:text-gray-700'}`}>
                            {r.override ? 'Different hours ✓' : 'Different hours'}
                          </button>
                        </>
                      )}
                    </div>

                    {r.checked && r.override && (
                      <div className="flex flex-wrap items-end gap-3 mt-2 pl-6">
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-500">Start</span>
                          <input type="time" value={r.start} onChange={e => patchRow(emp.id, { start: e.target.value })}
                            className="h-8 px-2 rounded-lg border border-gray-300 text-sm" />
                        </label>
                        <label className="flex flex-col gap-1">
                          <span className="text-[11px] font-medium text-gray-500">End</span>
                          <input type="time" value={r.end} onChange={e => patchRow(emp.id, { end: e.target.value })}
                            className="h-8 px-2 rounded-lg border border-gray-300 text-sm" />
                        </label>
                        <label className="flex items-center gap-2 h-8">
                          <input type="checkbox" checked={r.lunch} onChange={e => patchRow(emp.id, { lunch: e.target.checked })} />
                          <span className="text-xs text-gray-700">30-min lunch</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
              {employees.length === 0 && (
                <p className="text-sm text-gray-400">No active workers found.</p>
              )}
            </div>
          </div>
          </>
          )}

          {/* Notes */}
          <textarea
            placeholder={noWork ? 'Reason (optional)' : 'Notes for the day (optional)'}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm"
          />

          {/* Already logged this day */}
          {existing.length > 0 && (
            <div className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Already logged this day
              </div>
              <ul className="divide-y divide-gray-50">
                {existing.map(en => (
                  <li key={en.id} className="flex items-center gap-3 px-4 py-2 text-sm">
                    <span className="font-medium text-gray-900 flex-1 truncate">{en.employee_name}</span>
                    {en.start_time && en.end_time && (
                      <span className="text-xs text-gray-400">{hhmm(en.start_time)}–{hhmm(en.end_time)}</span>
                    )}
                    <span className="font-semibold text-gray-900 tabular-nums">{Number(en.hours).toFixed(2)}h</span>
                    <button onClick={() => handleDelete(en.id)} className="text-gray-400 hover:text-red-600" title="Delete entry">
                      <span className="material-symbols-outlined text-base">delete</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between gap-3">
          <div className="text-sm text-gray-600">
            {noWork
              ? <span className="text-amber-700 font-medium">Day marked as no work</span>
              : selected.length > 0
                ? <>{selected.length} worker{selected.length !== 1 ? 's' : ''} · <span className="font-bold text-gray-900">{totalHours.toFixed(2)} h</span> total</>
                : <span className="text-gray-400">Select workers to log</span>}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="h-9 px-4 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-100">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || (!noWork && selected.length === 0)}
              className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-lg text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50 ${noWork ? 'bg-amber-600' : 'bg-primary'}`}>
              <span className={`material-symbols-outlined text-base ${saving ? 'animate-spin' : ''}`}>{saving ? 'progress_activity' : (noWork ? 'event_busy' : 'check')}</span>
              {saving ? 'Saving…' : (noWork ? 'Mark no work' : 'Log hours')}
            </button>
          </div>
        </div>
      </div>
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
